import { getCompanySlug } from '../../../client-experience/utils/companyAccess';
import { GlobalUserService } from '../../storefront-shared/services/GlobalUserService';
import { getPublicClient, upsertPublicClient } from '../../../api/companyRecords';

const EMPTY_EMERGENCY_CONTACT = {
  name: '',
  phone: '',
  relationship: ''
};

export const parseCustomerName = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ')
  };
};

export const getCompanyClientDocumentStoragePath = (companyId, clientId, fileName) => (
  `client-portal/${companyId}/documents/${clientId}/${fileName}`
);

export async function saveClientAvatarUrl(companyId, userId, avatarUrl) {
  if (!companyId || !userId) {
    throw new Error('Missing company or user for avatar save.');
  }

  await upsertPublicClient({
    companyId,
    id: userId,
    record: {
      avatarUrl: avatarUrl || null,
      globalUserId: userId,
      updatedAt: new Date().toISOString(),
    },
  });

  localStorage.setItem('clientAvatarUrl', avatarUrl || '');
}

/** Update UI immediately; persist avatar URL on the public client record without blocking the caller. */
export function persistClientAvatarUrl(companyId, userId, avatarUrl, { onSaved, onRefresh, onPersistError }) {
  onSaved?.(avatarUrl);

  void saveClientAvatarUrl(companyId, userId, avatarUrl).catch((error) => {
    console.warn('Background avatar persist failed:', error);
    if (error?.code === 'permission-denied') {
      onPersistError?.(error);
    }
  });

  if (onRefresh) {
    void Promise.resolve(onRefresh()).catch((error) => {
      console.warn('Avatar refresh failed:', error);
    });
  }
}

export const persistPortalClientSession = (client) => {
  if (!client?.id || !client?.companyId || !client?.email) {
    return;
  }

  localStorage.setItem('clientId', client.id);
  localStorage.setItem('clientEmail', client.email);
  localStorage.setItem('clientFirstName', client.firstName || '');
  localStorage.setItem('clientLastName', client.lastName || '');
  localStorage.setItem('clientPhone', client.phone || '');
  localStorage.setItem('clientName', client.name || '');
  localStorage.setItem('clientCompanyId', client.companyId);
  localStorage.setItem('clientPortalUrl', client.portalUrl || '');
  localStorage.setItem('clientPortalCompanyId', client.companyId);
  localStorage.setItem('clientAvatarUrl', client.avatarUrl || '');
};

export const clearPortalClientSession = () => {
  [
    'clientId',
    'clientEmail',
    'clientFirstName',
    'clientLastName',
    'clientPhone',
    'clientName',
    'clientCompanyId',
    'clientPortalUrl',
    'clientPortalCompanyId',
    'clientAvatarUrl'
  ].forEach((key) => localStorage.removeItem(key));
};

const buildClientSnapshot = ({
  sessionUser,
  companyData,
  globalUser,
  companyClientData
}) => {
  const firstName = companyClientData?.firstName || globalUser?.firstName || '';
  const lastName = companyClientData?.lastName || globalUser?.lastName || '';
  const name = companyClientData?.name || globalUser?.name || `${firstName} ${lastName}`.trim();
  const phone = companyClientData?.phone || globalUser?.phone || '';

  return {
    id: sessionUser.uid,
    uid: sessionUser.uid,
    email: sessionUser.email || companyClientData?.email || globalUser?.email || '',
    firstName,
    lastName,
    name,
    phone,
    address: companyClientData?.address || globalUser?.address || null,
    smsConsent: companyClientData?.smsConsent ?? globalUser?.smsConsent ?? false,
    avatarUrl: companyClientData?.avatarUrl || null,
    emergencyContact: companyClientData?.emergencyContact || EMPTY_EMERGENCY_CONTACT,
    companyId: companyData.id,
    portalUrl: getCompanySlug(companyData),
    company: {
      id: companyData.id,
      name: companyData.name,
      portalUrl: getCompanySlug(companyData)
    }
  };
};

export const buildClientSyncData = ({
  sessionUser,
  companyData,
  globalUser,
  existingClientData = {},
  overrides = {}
}) => {
  const mergedName = overrides.name
    || existingClientData.name
    || globalUser?.name
    || sessionUser.displayName
    || `${overrides.firstName || globalUser?.firstName || ''} ${overrides.lastName || globalUser?.lastName || ''}`.trim();
  const parsedName = parseCustomerName(mergedName);
  const firstName = overrides.firstName || parsedName.firstName || existingClientData.firstName || globalUser?.firstName || '';
  const lastName = overrides.lastName || parsedName.lastName || existingClientData.lastName || globalUser?.lastName || '';
  const email = overrides.email || sessionUser.email || existingClientData.email || globalUser?.email || '';
  const phone = overrides.phone ?? existingClientData.phone ?? globalUser?.phone ?? '';

  const syncData = {
    email,
    firstName,
    lastName,
    name: overrides.name || `${firstName} ${lastName}`.trim(),
    searchName: (overrides.name || `${firstName} ${lastName}`.trim()).toLowerCase(),
    phone,
    smsConsent: overrides.smsConsent ?? existingClientData.smsConsent ?? globalUser?.smsConsent ?? false,
    status: existingClientData.status || 'active',
    archived: existingClientData.archived ?? false,
    emergencyContact: existingClientData.emergencyContact || EMPTY_EMERGENCY_CONTACT,
    avatarUrl: existingClientData.avatarUrl || sessionUser.photoURL || '',
    portalUrl: getCompanySlug(companyData),
    updatedAt: new Date().toISOString(),
    lastLogin: new Date().toISOString()
  };

  if (!existingClientData.createdAt) {
    syncData.createdAt = new Date().toISOString();
    syncData.loyaltyPoints = existingClientData.loyaltyPoints ?? 0;
    syncData.totalSpent = existingClientData.totalSpent ?? 0;
    syncData.appointmentCount = existingClientData.appointmentCount ?? 0;
  }

  return syncData;
};

export const resolveStorefrontClient = async (authUser, companyData) => {
  if (!authUser || !companyData?.id) {
    return {
      isAuthenticated: false,
      hasPortalAccess: false,
      storefrontClient: null,
      sessionUser: null
    };
  }

  const globalUser = await GlobalUserService.getGlobalUser(authUser.uid);
  const sessionUser = {
    uid: authUser.uid,
    email: authUser.email || '',
    displayName: authUser.displayName || '',
    photoURL: authUser.photoURL || null,
    accountType: globalUser?.accountType || null
  };

  if (globalUser?.accountType === 'company') {
    return {
      isAuthenticated: true,
      hasPortalAccess: false,
      storefrontClient: null,
      sessionUser,
      reason: 'company-account'
    };
  }

  const hasPortalAccess = await GlobalUserService.userHasCompanyAccess(sessionUser.uid, companyData.id);
  if (!hasPortalAccess) {
    return {
      isAuthenticated: true,
      hasPortalAccess: false,
      storefrontClient: null,
      sessionUser,
      reason: 'missing-company-access'
    };
  }

  let existingClientData = {};
  try {
    existingClientData = await getPublicClient({
      companyId: companyData.id,
      id: sessionUser.uid,
    }) || {};
  } catch (error) {
    if (error.status !== 404) {
      console.warn('Could not load storefront company client record.', error);
    }
  }
  const mergedGlobalUser = globalUser || {
    email: sessionUser.email || '',
    firstName: '',
    lastName: '',
    name: sessionUser.displayName || '',
    phone: ''
  };

  if (!existingClientData.id) {
    try {
      await GlobalUserService.syncCompanyClientData(
        sessionUser.uid,
        companyData.id,
        buildClientSyncData({
          sessionUser,
          companyData,
          globalUser: mergedGlobalUser,
          existingClientData
        })
      );

      existingClientData = await getPublicClient({
        companyId: companyData.id,
        id: sessionUser.uid,
      }) || existingClientData;
    } catch (error) {
      console.warn('Could not backfill storefront company client record during auth resolution.', {
        code: error?.code,
        message: error?.message,
        raw: error
      });
    }
  }

  const storefrontClient = buildClientSnapshot({
    sessionUser,
    companyData,
    globalUser: mergedGlobalUser,
    companyClientData: existingClientData
  });

  return {
    isAuthenticated: true,
    hasPortalAccess: true,
    storefrontClient,
    sessionUser,
    reason: null
  };
};
